import { Calendar, MapPin, Bookmark, Clock, Building2, Phone, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Opportunity } from '@/types';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface OpportunityListProps {
  opportunities: Opportunity[];
}

const typeLabels = {
  emploi: 'Emploi',
  stage: 'Stage',
  bourse: 'Bourse',
  formation: 'Formation',
  volontariat: 'Volontariat'
};

const typeColors = {
  emploi: 'bg-primary text-primary-foreground',
  stage: 'bg-secondary text-secondary-foreground',
  bourse: 'bg-success text-success-foreground',
  formation: 'bg-accent text-accent-foreground',
  volontariat: 'bg-destructive text-destructive-foreground'
};

const contractLabels = {
  cdi: 'CDI',
  cdd: 'CDD',
  'temps-plein': 'Temps plein',
  'temps-partiel': 'Temps partiel',
  freelance: 'Freelance'
};

export function OpportunityList({ opportunities }: OpportunityListProps) {
  if (opportunities.length === 0) {
    return (
      <Card className="p-12 text-center animate-fade-in">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Aucune opportunité trouvée</h3>
          <p className="text-muted-foreground mb-4">
            Aucune opportunité ne correspond à vos critères de recherche.
          </p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Réinitialiser les filtres
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {opportunities.map((opportunity, index) => (
        <Card 
          key={opportunity.id} 
          className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1 animate-fade-in"
          style={{ animationDelay: `${index * 50}ms` }}
        >
          <div className="p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              {/* Main Content */}
              <div className="flex-1">
                <div className="flex items-start gap-4">
                  {/* Company Logo */}
                  <div className="hidden md:flex w-14 h-14 bg-muted rounded-lg items-center justify-center shrink-0 transition-colors hover:bg-primary/10">
                    <Building2 className="h-6 w-6 text-muted-foreground" />
                  </div>
                  
                  <div className="flex-1">
                    {/* Header */}
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Badge className={typeColors[opportunity.type]}>
                        {typeLabels[opportunity.type]}
                      </Badge>
                      {opportunity.contractType && (
                        <Badge variant="outline">
                          {contractLabels[opportunity.contractType]}
                        </Badge>
                      )}
                      {opportunity.featured && (
                        <Badge className="bg-gradient-gold text-primary animate-pulse">
                          À la une
                        </Badge>
                      )}
                    </div>
                    
                    {/* Title & Company */}
                    <h3 className="text-xl font-poppins font-semibold text-foreground mb-1 hover:text-primary transition-colors">
                      {opportunity.title}
                    </h3>
                    <p className="text-muted-foreground font-inter mb-3">{opportunity.company}</p>
                    
                    {/* Description */}
                    <p className="text-muted-foreground font-inter mb-4 line-clamp-2">
                      {opportunity.description}
                    </p>
                    
                    {/* Meta Information */}
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1 hover:text-primary transition-colors">
                        <MapPin className="h-4 w-4" />
                        <span className="font-inter">
                          {opportunity.arrondissement || opportunity.location}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span className="font-inter">
                          Publié {format(opportunity.publishedAt, 'dd MMMM', { locale: fr })}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-destructive">
                        <Clock className="h-4 w-4" />
                        <span className="font-inter font-semibold">
                          {format(opportunity.deadline, 'dd/MM/yyyy')}
                        </span>
                      </div>
                      {opportunity.salary && (
                        <div className="text-success font-semibold font-inter">
                          {opportunity.salary}
                        </div>
                      )}
                      {opportunity.phoneNumber && (
                        <div className="flex items-center gap-1">
                          <Phone className="h-4 w-4" />
                          <span className="font-inter">
                            {opportunity.phoneNumber}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex md:flex-col gap-2">
                <Link to={`/opportunity/${opportunity.id}`} className="flex-1 md:flex-initial">
                  <Button variant="default" size="sm" className="w-full group">
                    <span>Voir détails</span>
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                {opportunity.applicationUrl ? (
                  <a 
                    href={opportunity.applicationUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex-1 md:flex-initial"
                  >
                    <Button variant="secondary" size="sm" className="w-full hover:scale-105 transition-transform">
                      Postuler
                    </Button>
                  </a>
                ) : (
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    className="flex-1 md:flex-initial hover:scale-105 transition-transform"
                    disabled
                  >
                    Postuler
                  </Button>
                )}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="md:mt-2 hover:text-primary transition-colors"
                  title="Sauvegarder"
                >
                  <Bookmark className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}